import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { authSelector } from "../../reducers";
import { storageUploadImage } from "../../api";
import { IconUploadCloud } from "../icons";
import { eSalaryImage } from "../utils.jsx";
import { Button } from "../Button.tsx";

export function ImageUpload({ currentImage, entityId, savedKey, logo }) {
  const auth = useSelector(authSelector);

  const divRef = useRef();
  const cropCanvasRef = useRef();
  const [editMode, setEditMode] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [img, setImg] = useState(null);
  //savedKey(res.key);

  function onPaste(e) {
    if (!e.clipboardData || !e.clipboardData.files || e.clipboardData.files.length === 0) {
      return;
    }
    setImageFile(e.clipboardData.files[0]);
  }

  useEffect(() => {
    document.addEventListener("paste", onPaste);
    return () => {
      if (img) {
        URL.revokeObjectURL(img.src);
      }
      document.removeEventListener("paste", onPaste);
    };
  }, []);

  const isCircle = !logo;
  let crop = null;
  let isDragging = false;
  let dragStart = { x: 0, y: 0 };

  async function resizeImage() {
    const MAX_WIDTH = 512;
    const MAX_HEIGHT = 512;
    const resizeQuality = "high"; // "pixelated", "low", "medium", "high"
    const saveFormat = "image/webp";
    const compressionQuality = 0.9; // 0 to 1

    let sx = 0,
      sy = 0,
      sw = img.width,
      sh = img.height;
    if (crop) {
      if (crop.r !== undefined && crop.r > 5) {
        sx = Math.max(0, Math.floor(crop.cx - crop.r));
        sy = Math.max(0, Math.floor(crop.cy - crop.r));
        sw = Math.min(img.width - sx, Math.ceil(crop.r * 2));
        sh = Math.min(img.height - sy, Math.ceil(crop.r * 2));
      } else if (crop.w !== undefined && Math.abs(crop.w) > 5 && Math.abs(crop.h) > 5) {
        sx = Math.min(crop.x, crop.x + crop.w);
        sy = Math.min(crop.y, crop.y + crop.h);
        sw = Math.max(1, Math.abs(crop.w));
        sh = Math.max(1, Math.abs(crop.h));
      }
    }

    let width = sw,
      height = sh;
    if (width > height) {
      if (width > MAX_WIDTH) {
        height = height * (MAX_WIDTH / width);
        width = MAX_WIDTH;
      }
    } else {
      if (height > MAX_HEIGHT) {
        width = width * (MAX_HEIGHT / height);
        height = MAX_HEIGHT;
      }
    }

    try {
      // Crop the selected area to a temp canvas, then resize
      const tempCanvas = new OffscreenCanvas(sw, sh);
      const tctx = tempCanvas.getContext("2d");
      tctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

      // Now resize using createImageBitmap for quality
      const bitmap = await createImageBitmap(tempCanvas, {
        resizeWidth: Math.floor(width),
        resizeHeight: Math.floor(height),
        resizeQuality,
      });
      const ocanvas = new OffscreenCanvas(Math.floor(width), Math.floor(height));
      ocanvas.getContext("bitmaprenderer").transferFromImageBitmap(bitmap);
      let blob = await ocanvas.convertToBlob({ type: saveFormat, quality: compressionQuality });
      if (blob.type !== saveFormat) {
        console.warn("convertToBlob failed, got", blob.type); // Safari does not support WebP
        blob = await ocanvas.convertToBlob({ type: "image/jpeg", quality: compressionQuality });
      }

      const onUploadProgress = (progressEvent) => {
        console.log("progressEvent", Math.round((progressEvent.loaded / progressEvent.total) * 100));
        //setList(currentList => [...currentList]); // update progress bar
      };

      const meta = {
        // metadata is sent in request headers
        section: logo ? "logo" : "profilepic",
        entityid: entityId, // id of the entity saved before attachement upload
        filename: encodeURIComponent(imageFile.name), // original filename
        lastmodified: imageFile.lastModified, // original file last modified timestamp
      };

      // TODO: loading indicator

      // for SVG without cropping, use original file
      if (!crop && imageFile.type === "image/svg+xml") {
        blob = new Blob([await imageFile.arrayBuffer()], { type: imageFile.type });
      }

      // Upload the image and get the saved key
      meta["Content-Type"] = blob.type;
      const res = await storageUploadImage(meta, blob, onUploadProgress);
      savedKey(res.data.key);

      setEditMode(false);
      setImageFile(null);
      setImg(null);
      crop = null;

      console.log(
        "Cropped & Resized:",
        blob.type,
        "Dimensions:",
        Math.floor(width),
        "x",
        Math.floor(height),
        "Size:",
        blob.size,
      );
    } catch (err) {
      console.error("createImageBitmap error:", err);
    }
  }

  function pointerDown(e) {
    const cropCanvas = cropCanvasRef.current;
    const rect = cropCanvas.getBoundingClientRect();
    isDragging = true;
    const cx = Math.round((e.clientX - rect.left) * (cropCanvas.width / rect.width));
    const cy = Math.round((e.clientY - rect.top) * (cropCanvas.height / rect.height));

    if (isCircle) {
      dragStart = { x: cx, y: cy };
      crop = { cx, cy, r: 0 };
    } else {
      dragStart = { x: cx, y: cy };
      crop = { x: cx, y: cy, w: 0, h: 0 };
    }

    if (e.pointerId && typeof cropCanvas.setPointerCapture === "function") {
      try {
        cropCanvas.setPointerCapture(e.pointerId);
      } catch (_) {}
    }
    window.addEventListener("pointermove", pointerMove);
    window.addEventListener("pointerup", pointerUp);
    window.addEventListener("pointercancel", pointerUp);

    drawCropCanvas();
  }

  function pointerMove(e) {
    if (!isDragging || !crop) return;
    const cropCanvas = cropCanvasRef.current;

    const rect = cropCanvas.getBoundingClientRect();
    const rawX = Math.round((e.clientX - rect.left) * (cropCanvas.width / rect.width));
    const rawY = Math.round((e.clientY - rect.top) * (cropCanvas.height / rect.height));

    if (isCircle && crop.cx !== undefined) {
      const dx = rawX - crop.cx;
      const dy = rawY - crop.cy;
      const rawR = Math.round(Math.hypot(dx, dy));
      const maxLeft = crop.cx;
      const maxTop = crop.cy;
      const maxRight = img.width - crop.cx;
      const maxBottom = img.height - crop.cy;
      const rMax = Math.max(0, Math.floor(Math.min(maxLeft, maxTop, maxRight, maxBottom)));
      crop.r = Math.min(rawR, rMax);
    } else if (!isCircle && crop.x !== undefined) {
      // clamp pointer position to image bounds so hitting top/left doesn't cancel the crop
      const x = Math.max(0, Math.min(img.width, rawX));
      const y = Math.max(0, Math.min(img.height, rawY));

      // compute width/height relative to drag start
      crop.w = x - dragStart.x;
      crop.h = y - dragStart.y;

      // keep crop.x/crop.y as the top-left of the original drag start when width/height negative
      // we keep original crop.x/crop.y values and let drawing use min(x, x+w)
      // ensure we don't exceed image bounds on the other side
      const nx = Math.min(dragStart.x, dragStart.x + crop.w);
      const ny = Math.min(dragStart.y, dragStart.y + crop.h);
      if (nx < 0) {
        // if left edge would be negative, clamp width so left edge is 0
        const overlap = 0 - nx;
        if (crop.w < 0) crop.w = crop.w + overlap;
        else {
          crop.x = 0;
          crop.w = dragStart.x + crop.w - 0;
        }
      }
      if (ny < 0) {
        const overlap = 0 - ny;
        if (crop.h < 0) crop.h = crop.h + overlap;
        else {
          crop.y = 0;
          crop.h = dragStart.y + crop.h - 0;
        }
      }
      // clamp right/bottom
      if (dragStart.x + crop.w > img.width) crop.w = img.width - dragStart.x;
      if (dragStart.y + crop.h > img.height) crop.h = img.height - dragStart.y;
    }
    drawCropCanvas();
  }

  function pointerUp(e) {
    isDragging = false;
    window.removeEventListener("pointermove", pointerMove);
    window.removeEventListener("pointerup", pointerUp);
    window.removeEventListener("pointercancel", pointerUp);

    const cropCanvas = cropCanvasRef.current;
    if (e && e.pointerId && typeof cropCanvas.releasePointerCapture === "function") {
      try {
        cropCanvas.releasePointerCapture(e.pointerId);
      } catch (_) {}
    }

    if (!(crop && ((isCircle && crop.r > 5) || (!isCircle && Math.abs(crop.w) > 5 && Math.abs(crop.h) > 5)))) {
      crop = null;
      drawCropCanvas();
    }
    //resizeImage();
  }

  function drawCropCanvas() {
    const cropCanvas = cropCanvasRef.current;

    const ctx = cropCanvas.getContext("2d");
    ctx.clearRect(0, 0, cropCanvas.width, cropCanvas.height);
    ctx.drawImage(img, 0, 0);

    // compute a scale so we can keep line width and dash length constant visually (CSS pixels)
    const cssScale = cropCanvas.width / Math.max(1, cropCanvas.clientWidth);
    const desiredCssLinePx = 1.25; // desired visual thickness in CSS pixels
    const visualLineWidth = Math.max(1, cssScale * desiredCssLinePx);
    const desiredCssDashPx = 6; // desired dash length in CSS pixels
    const dashPx = Math.max(1, cssScale * desiredCssDashPx);

    // Draw overlay if cropping
    if (crop) {
      if (isCircle && crop.r > 5) {
        ctx.save();

        // darken whole canvas
        ctx.fillStyle = "rgba(0,0,0,.4)";
        ctx.fillRect(0, 0, cropCanvas.width, cropCanvas.height);

        // Circle center and radius
        const cx = crop.cx;
        const cy = crop.cy;
        const r = crop.r;

        // Clip to circle and draw the image inside the circle (show original)
        ctx.beginPath();
        ctx.ellipse(cx, cy, r, r, 0, 0, 2 * Math.PI);
        ctx.clip();

        // Compute bounding square for the circle to draw that portion of the image
        const sx = Math.max(0, Math.floor(cx - r));
        const sy = Math.max(0, Math.floor(cy - r));
        const sw = Math.min(img.width - sx, Math.ceil(r * 2));
        const sh = Math.min(img.height - sy, Math.ceil(r * 2));
        if (sw > 0 && sh > 0) {
          ctx.drawImage(img, sx, sy, sw, sh, sx, sy, sw, sh);
        }
        ctx.restore();

        // Draw circle border with scaled dash
        ctx.save();
        ctx.strokeStyle = "#7EA4CD";
        ctx.lineWidth = visualLineWidth;
        ctx.setLineDash([dashPx]);
        ctx.beginPath();
        ctx.ellipse(cx, cy, r, r, 0, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.restore();

        // Draw two evenly spaced vertical + two evenly spaced horizontal lines (grid inside circle)
        ctx.save();
        ctx.strokeStyle = "#7EA4CD";
        ctx.lineWidth = Math.max(1, visualLineWidth / 2); // thinner for crosshairs
        // use a smaller dash for crosshair but keep scaled
        ctx.setLineDash([Math.max(1, dashPx / 2)]);

        const offs = r / 3; // two lines at ±r/3 from center -> three even columns/rows
        const yExtent = (dx) => Math.sqrt(Math.max(0, r * r - dx * dx));

        // vertical lines at cx - offs and cx + offs (clipped to circle)
        const vx1 = cx - offs;
        const vx2 = cx + offs;
        const vyExtent = yExtent(offs);
        ctx.beginPath();
        ctx.moveTo(vx1, cy - vyExtent);
        ctx.lineTo(vx1, cy + vyExtent);
        ctx.moveTo(vx2, cy - vyExtent);
        ctx.lineTo(vx2, cy + vyExtent);

        // horizontal lines at cy - offs and cy + offs (clipped to circle)
        const hy1 = cy - offs;
        const hy2 = cy + offs;
        const hxExtent = yExtent(offs);
        ctx.moveTo(cx - hxExtent, hy1);
        ctx.lineTo(cx + hxExtent, hy1);
        ctx.moveTo(cx - hxExtent, hy2);
        ctx.lineTo(cx + hxExtent, hy2);

        ctx.stroke();
        ctx.restore();
      } else if (!isCircle && Math.abs(crop.w) > 5 && Math.abs(crop.h) > 5) {
        // rectangle overlay: darken and clear rect area
        ctx.save();
        ctx.fillStyle = "rgba(0,0,0,.4)";
        ctx.fillRect(0, 0, cropCanvas.width, cropCanvas.height);

        const x = Math.min(crop.x, crop.x + crop.w);
        const y = Math.min(crop.y, crop.y + crop.h);
        const w = Math.abs(crop.w);
        const h = Math.abs(crop.h);

        // show original inside selection
        ctx.drawImage(img, x, y, w, h, x, y, w, h);

        // draw rectangle border (use scaled dash)
        ctx.strokeStyle = "#f00";
        ctx.lineWidth = visualLineWidth;
        ctx.setLineDash([dashPx]);
        ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
        ctx.restore();
      }
    }
  }

  useEffect(() => {
    if (img) {
      const cropCanvas = cropCanvasRef.current;
      cropCanvas.width = img.width;
      cropCanvas.height = img.height;
      cropCanvas.style.maxWidth = "100%";
      cropCanvas.style.maxHeight = "400px";
      cropCanvas.style.outline = "1px solid #ccc";
      cropCanvas.style.cursor = "crosshair";
      drawCropCanvas();
      crop = null;
    }
  }, [img]);

  function loadImageFile() {
    const reader = new FileReader();
    reader.onload = (e) => {
      const image = new Image();
      image.onload = () => {
        setEditMode(true);
        setImg(image);
      };
      const blob = new Blob([e.target.result], { type: imageFile.type });
      image.src = URL.createObjectURL(blob);
    };
    reader.readAsArrayBuffer(imageFile);
  }

  useEffect(() => {
    if (imageFile) {
      loadImageFile();
      //setEditMode(true);
    }
  }, [imageFile]);

  function dragEnterOver(e) {
    preventDefault(e);
    divRef.current.style.background = "#c0c0c0";
    divRef.current.style.outline = "2px dashed red";
  }
  function dragLeave(e) {
    preventDefault(e);
    divRef.current.style.background = null;
    divRef.current.style.outline = null;
  }
  function drop(e) {
    dragLeave(e);
    const { files } = e.dataTransfer;
    if (files && files[0]) {
      setImageFile(files[0]);
    } else {
      const url = e.dataTransfer.getData("text/plain");
      if (url) {
        fetch(url)
          .then((res) => res.blob())
          .then((blob) => {
            setImageFile(new File([blob], "image", { type: blob.type }));
          })
          .catch((err) => {
            console.error(err);
          });
      }
    }
  }

  return (
    <div
      style={{
        border: "1px solid #ccc",
        padding: "20px",
        borderRadius: "5px",
        textAlign: "center",
        background: "white",
        height: "100%",
        width: "100%",
      }}
      ref={divRef}
      onDragEnter={dragEnterOver}
      onDragOver={dragEnterOver}
      onDragLeave={dragLeave}
      onDrop={drop}
    >
      {editMode ? (
        <>
          <p>Save the image or crop and save</p>
          <canvas ref={cropCanvasRef} onPointerDown={pointerDown}></canvas>
          <br />
          <Button testId="cancel" variant="secondary" onClick={() => setEditMode(false)}>
            Cancel
          </Button>
          <Button testId="save" variant="primary" className="ms-2" onClick={resizeImage}>
            Save
          </Button>
        </>
      ) : (
        <>
          {currentImage ? (
            <img
              src={eSalaryImage(auth.eSalaryCompanyId, currentImage)}
              crossOrigin="anonymous"
              alt="Image"
              style={{ marginBottom: "10px", maxWidth: "100%" }}
            />
          ) : (
            <div
              style={{
                marginBottom: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "150px",
                color: "#999",
              }}
            >
              <IconUploadCloud size={48} />
            </div>
          )}
          <Button
            testId="upload-image"
            variant="primary"
            style={{ width: "100%" }}
            onClick={() => {
              const i = document.createElement("input");
              i.type = "file";
              i.accept = "image/*";
              i.onchange = (e) => {
                const { files } = e.target;
                if (files && files[0]) {
                  setImageFile(files[0]);
                }
              };
              i.click();
            }}
          >
            Upload, paste or drag & drop image
          </Button>
        </>
      )}
    </div>
  );
}

function preventDefault(e) {
  e.preventDefault();
  e.stopPropagation();
}
